import type { FormEvent } from 'react';
import { useTodosQuery, useCreateTodoMutation } from '@/features/todos/todos-hooks';
import type { Todo } from '@app/server/src/features/todos/todos-schema';
import { Button } from '@/components/ui/button';
import { Field, FieldTitle, FieldGroup, FieldError } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

const LoadingState = () => <div>Loading...</div>;
const ErrorState = ({ message }: { message: string }) => <div>Error: {message}</div>;
const EmptyState = () => <div>No todos yet. Add one!</div>;

const getTodoItemStyle = (completed: boolean) => ({
  textDecoration: completed ? 'line-through' : 'none',
});

const TodoItem = ({ todo }: { todo: Todo }) => (
  <li style={getTodoItemStyle(todo.completed)}>{todo.text}</li>
);

const renderTodo = (todo: Todo) => <TodoItem key={todo.id} todo={todo} />;
const TodoItems = ({ todos }: { todos: Todo[] }) => {
  if (todos.length === 0) return <EmptyState />;
  return <ul>{todos.map((todo) => renderTodo(todo))}</ul>;
};

type TodoFormProps = {
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  isSubmitting: boolean;
  error?: string;
};
const TodoForm = ({ onSubmit, isSubmitting }: TodoFormProps) => (
  <form onSubmit={onSubmit}>
    <FieldGroup className="my-6 max-w-sm">
      <Field orientation="horizontal">
        <Input name="text" placeholder="New Todo" required />
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Adding...' : 'Add Todo'}
        </Button>
      </Field>
    </FieldGroup>
  </form>
);

export const TodoList = () => {
  const todosQuery = useTodosQuery();
  const createTodo = useCreateTodoMutation();

  const handleCreateTodo = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const text = (formData.get('text') as string).trim();
    if (text) {
      createTodo.mutate(text);
      event.currentTarget.reset();
    }
  };

  if (todosQuery.isLoading) return <LoadingState />;
  if (todosQuery.error) return <ErrorState message={todosQuery.error.message} />;

  return (
    <div>
      <FieldTitle className="my-4 text-2xl font-semibold">
        Todos ({todosQuery.data?.length ?? 0})
      </FieldTitle>
      <TodoItems todos={todosQuery.data ?? []} />
      <TodoForm onSubmit={handleCreateTodo} isSubmitting={createTodo.isPending} />
      <FieldError errors={[{ message: createTodo.error?.message }]} />
    </div>
  );
};
